import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Users } from "lucide-react";
import { supabase } from "../lib/supabase";
import { SESSIONS, SESSION_LIST } from "../data/liveSessionData";

const generateCode = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
};

const LiveSessionPresenter = ({ onBack, userData }) => {
  const [phase, setPhase] = useState("setup"); // setup | lobby | slides | quiz | ended
  const [sessionData, setSessionData] = useState(null);
  const [dbSession, setDbSession] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [slideIndex, setSlideIndex] = useState(0);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [questionRevealed, setQuestionRevealed] = useState(false);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const joinUrl = dbSession ? `${window.location.origin}/join/${dbSession.code}` : "";

  // Subscribe to new participants and responses once session exists
  useEffect(() => {
    if (!dbSession?.id) return;

    const participantSub = supabase
      .channel(`presenter-participants-${dbSession.id}`)
      .on("postgres_changes", {
        event: "INSERT", schema: "public", table: "session_participants",
        filter: `session_id=eq.${dbSession.id}`,
      }, (payload) => setParticipants((prev) => [...prev, payload.new]))
      .subscribe();

    const responseSub = supabase
      .channel(`presenter-responses-${dbSession.id}`)
      .on("postgres_changes", {
        event: "INSERT", schema: "public", table: "session_responses",
        filter: `session_id=eq.${dbSession.id}`,
      }, (payload) => setResponses((prev) => [...prev, payload.new]))
      .subscribe();

    return () => {
      supabase.removeChannel(participantSub);
      supabase.removeChannel(responseSub);
    };
  }, [dbSession?.id]);

  const updateSession = async (updates) => {
    if (!dbSession) return;
    const { data } = await supabase
      .from("live_sessions")
      .update(updates)
      .eq("id", dbSession.id)
      .select()
      .single();
    if (data) setDbSession(data);
  };

  const createSession = async (topicId) => {
    setLoading(true);
    setError("");
    const { data: { user } } = await supabase.auth.getUser();
    const code = generateCode();
    const { data, error: err } = await supabase
      .from("live_sessions")
      .insert({ code, presenter_id: user.id, topic: topicId, status: "waiting", current_slide: 0, current_question: -1, question_revealed: false })
      .select()
      .single();
    if (err) { setError("Failed to create session. Please try again."); setLoading(false); return; }
    setSessionData(SESSIONS[topicId]);
    setDbSession(data);
    setPhase("lobby");
    setLoading(false);
  };

  const startSlides = async () => {
    await updateSession({ status: "slides", current_slide: 0 });
    setSlideIndex(0);
    setPhase("slides");
  };

  const goToSlide = async (idx) => {
    setSlideIndex(idx);
    await updateSession({ current_slide: idx });
  };

  const startQuiz = async () => {
    setQuestionIndex(0);
    setQuestionRevealed(false);
    setResponses([]);
    await updateSession({ status: "quiz", current_question: 0, question_revealed: false });
    setPhase("quiz");
  };

  const revealAnswer = async () => {
    setQuestionRevealed(true);
    await updateSession({ question_revealed: true });
  };

  const nextQuestion = async () => {
    const next = questionIndex + 1;
    if (next >= sessionData.questions.length) {
      await updateSession({ status: "ended" });
      setPhase("ended");
    } else {
      setQuestionIndex(next);
      setQuestionRevealed(false);
      await updateSession({ current_question: next, question_revealed: false });
    }
  };

  // ── SETUP ──────────────────────────────────────────────────────────────────
  if (phase === "setup") return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 p-4">
      <button onClick={onBack} className="text-white/60 hover:text-white flex items-center gap-1 text-sm mb-8">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-extrabold text-white mb-1">Live Guidelines Session</h1>
        <p className="text-white/50 mb-8">Choose a topic — students join via QR code on their phone.</p>
        {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
        <div className="space-y-3">
          {SESSION_LIST.map((s) => (
            <button key={s.id} onClick={() => createSession(s.id)} disabled={loading}
              className="w-full bg-white/10 hover:bg-white/20 border border-white/20 rounded-2xl p-5 text-left transition-all disabled:opacity-50">
              <div className="text-4xl mb-2">{s.emoji}</div>
              <div className="font-bold text-white text-lg">{s.topic}</div>
              <div className="text-white/40 text-sm mt-1">{s.slides.length} slides · {s.questions.length} questions</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // ── LOBBY ──────────────────────────────────────────────────────────────────
  if (phase === "lobby") return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 p-4">
      <div className="max-w-lg mx-auto pt-6">
        <div className="text-center mb-6">
          <div className="text-5xl mb-2">{sessionData.emoji}</div>
          <h1 className="text-2xl font-extrabold text-white">{sessionData.topic}</h1>
          <p className="text-white/50 text-sm mt-1">Share the code or QR — then start when ready</p>
        </div>

        <div className="bg-white rounded-3xl p-6 mb-5 text-center shadow-2xl">
          <p className="text-gray-400 text-xs mb-3 uppercase tracking-wider">Scan to join</p>
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(joinUrl)}&bgcolor=ffffff&color=1e1b4b`}
            alt="Join QR Code"
            className="mx-auto mb-4 rounded-xl border border-gray-100"
          />
          <p className="text-gray-400 text-xs mb-1">or visit <span className="font-mono text-gray-600">{window.location.host}/join</span> and enter</p>
          <div className="text-5xl font-extrabold tracking-[0.2em] text-indigo-700 font-mono mt-2">
            {dbSession?.code}
          </div>
        </div>

        <div className="bg-white/10 border border-white/20 rounded-2xl p-4 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-white/60" />
            <span className="text-white font-bold text-sm">{participants.length} participant{participants.length !== 1 ? "s" : ""} joined</span>
          </div>
          {participants.length === 0
            ? <p className="text-white/30 text-sm text-center py-2">No one yet — share the code!</p>
            : <div className="flex flex-wrap gap-2">
                {participants.map((p) => (
                  <span key={p.id} className="bg-white/20 text-white text-sm px-3 py-1 rounded-full">{p.name}</span>
                ))}
              </div>
          }
        </div>

        <button onClick={startSlides}
          className="w-full py-4 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-extrabold text-lg hover:from-indigo-400 hover:to-purple-400 transition-all shadow-xl">
          🚀 Start Presentation
        </button>
      </div>
    </div>
  );

  // ── SLIDES ─────────────────────────────────────────────────────────────────
  if (phase === "slides") {
    const slide = sessionData.slides[slideIndex];
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 p-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-4 pt-2">
            <span className="text-white/50 text-sm">{sessionData.emoji} {sessionData.topic}</span>
            <span className="text-white/50 text-sm flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {participants.length}</span>
          </div>

          <div className="bg-white rounded-3xl p-8 mb-5 shadow-2xl min-h-[380px] flex flex-col">
            <div className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-2">{slide.subtitle}</div>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-5">{slide.title}</h2>
            <div className="flex-1 text-gray-700 text-base leading-relaxed whitespace-pre-line">{slide.content}</div>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => goToSlide(slideIndex - 1)} disabled={slideIndex === 0}
              className="flex-1 py-3 rounded-2xl bg-white/10 text-white font-semibold disabled:opacity-30 hover:bg-white/20 transition-all flex items-center justify-center gap-2">
              <ChevronLeft className="w-5 h-5" /> Previous
            </button>
            <span className="text-white/50 text-sm shrink-0">{slideIndex + 1} / {sessionData.slides.length}</span>
            <button onClick={() => goToSlide(slideIndex + 1)} disabled={slideIndex === sessionData.slides.length - 1}
              className="flex-1 py-3 rounded-2xl bg-white/10 text-white font-semibold disabled:opacity-30 hover:bg-white/20 transition-all flex items-center justify-center gap-2">
              Next <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {slideIndex === sessionData.slides.length - 1 && (
            <button onClick={startQuiz}
              className="w-full py-4 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-extrabold text-lg hover:from-green-400 hover:to-emerald-400 transition-all">
              🧠 Start Quiz
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── QUIZ ───────────────────────────────────────────────────────────────────
  if (phase === "quiz") {
    const q = sessionData.questions[questionIndex];
    const questionResponses = responses.filter((r) => r.question_index === questionIndex);
    const counts = {};
    questionResponses.forEach((r) => { counts[r.answer] = (counts[r.answer] || 0) + 1; });

    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 p-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-4 pt-2">
            <span className="text-white/50 text-sm">Question {questionIndex + 1} / {sessionData.questions.length}</span>
            <span className="text-white/50 text-sm flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> {questionResponses.length} / {participants.length} answered
            </span>
          </div>

          <div className="bg-white rounded-3xl p-6 mb-4 shadow-2xl">
            <p className="font-extrabold text-gray-900 text-xl mb-5">{q.question}</p>
            <div className="space-y-2">
              {q.options.map((opt) => {
                const count = counts[opt.id] || 0;
                const pct = participants.length > 0 ? Math.round((count / participants.length) * 100) : 0;
                const isCorrect = opt.id === q.correct;
                return (
                  <div key={opt.id} className={`rounded-xl p-3 border-2 transition-all ${
                    questionRevealed && isCorrect ? "border-green-400 bg-green-50" :
                    questionRevealed ? "border-gray-200 bg-gray-50" : "border-gray-200 bg-gray-50"
                  }`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className={`font-semibold text-sm ${questionRevealed && isCorrect ? "text-green-700" : "text-gray-800"}`}>
                        {questionRevealed && isCorrect ? "✅ " : ""}{opt.id.toUpperCase()}. {opt.text}
                      </span>
                      {questionRevealed && (
                        <span className="text-sm font-bold text-gray-500 shrink-0 ml-2">{count} ({pct}%)</span>
                      )}
                    </div>
                    {questionRevealed && (
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className={`h-2 rounded-full transition-all duration-500 ${isCorrect ? "bg-green-500" : "bg-gray-400"}`}
                          style={{ width: `${pct}%` }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {questionRevealed && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 mb-4">
              <div className="text-xs font-bold text-blue-500 mb-1">📖 Explanation</div>
              <p className="text-blue-900 text-sm">{q.explanation}</p>
            </div>
          )}

          {!questionRevealed && (
            <div className="bg-white/10 border border-white/20 rounded-xl p-3 mb-4">
              <div className="flex justify-between text-white text-sm mb-2">
                <span>Waiting for responses</span>
                <span>{questionResponses.length} / {participants.length}</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-3">
                <div className="h-3 rounded-full bg-gradient-to-r from-indigo-400 to-purple-400 transition-all duration-300"
                  style={{ width: `${participants.length > 0 ? (questionResponses.length / participants.length * 100) : 0}%` }} />
              </div>
            </div>
          )}

          {!questionRevealed ? (
            <button onClick={revealAnswer}
              className="w-full py-4 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-extrabold text-lg hover:from-amber-400 hover:to-orange-400 transition-all">
              🔍 Reveal Answer
            </button>
          ) : (
            <button onClick={nextQuestion}
              className="w-full py-4 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-extrabold text-lg hover:from-indigo-400 hover:to-purple-400 transition-all">
              {questionIndex < sessionData.questions.length - 1 ? "➡️ Next Question" : "🏁 End Session"}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── ENDED ──────────────────────────────────────────────────────────────────
  if (phase === "ended") {
    const totalCorrect = responses.filter((r) => r.is_correct).length;
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-white rounded-3xl p-8 shadow-2xl text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-2">Session Complete!</h2>
          <p className="text-gray-500 mb-6">{sessionData.topic}</p>
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-indigo-50 rounded-2xl p-4 border-2 border-indigo-200">
              <div className="text-3xl font-extrabold text-indigo-600">{participants.length}</div>
              <div className="text-sm text-indigo-700 font-semibold">Participants</div>
            </div>
            <div className="bg-green-50 rounded-2xl p-4 border-2 border-green-200">
              <div className="text-3xl font-extrabold text-green-600">
                {responses.length > 0 ? Math.round((totalCorrect / responses.length) * 100) : 0}%
              </div>
              <div className="text-sm text-green-700 font-semibold">Correct Answers</div>
            </div>
          </div>
          <button onClick={onBack}
            className="w-full py-3 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-bold hover:from-indigo-400 hover:to-purple-400 transition-all">
            Back to Menu
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default LiveSessionPresenter;
